import React, { useRef } from 'react';
import { useResponsive } from '../hook/useResponsive';

interface Product {
  sku?: string;
  groupName?: string;
  productName?: string;
  uomName?: string;
  isAttributes?: boolean;
}

interface ProductSelectionModalProps {
  showPopup: boolean;
  onClose: () => void;
  products: Product[];
  filter: {
    searchValue: string;
  };
  onFilterChange: (filter: { searchValue: string }) => void;
  onSearchSubmit: (e: any) => void;
  onSelectProduct: (product: Product) => void;
  onCancel: () => void;
  onSave: () => void;
  // The page's kb.primaryProps for Save; without it Save just calls onSave.
  saveButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  loading?: boolean;
  language?: Record<string, string>;
  scrollRef?: React.RefObject<HTMLDivElement>;
  loaderRef?: (node: HTMLDivElement | null) => void;
  SearchIcon: React.ComponentType;
  CloseIcon: React.ComponentType;
  WhiteCheckIcon: React.ComponentType;
}

// Space toggles a row checkbox; Enter does nothing here (Ctrl/Cmd+Enter in the search, or Save, saves).
const toggleOnSpace = (toggle: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (e.key !== ' ' || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
  e.preventDefault();
  if (!e.repeat) toggle();
};

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  showPopup,
  onClose,
  products,
  filter,
  onFilterChange,
  onSearchSubmit,
  onSelectProduct,
  onCancel,
  onSave,
  saveButtonProps,
  loading = false,
  language,
  scrollRef,
  loaderRef,
  SearchIcon,
  CloseIcon,
  WhiteCheckIcon,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { isMobile, getWidth } = useResponsive();

  return (
    <div
      className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showPopup ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        } transition-opacity duration-300 ease-in-out`}
      style={{
        backgroundColor: '#22252552',
        zIndex: '200',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        width: '100%',
        height: '100vh',
      }}
    >
      <div
        className={`transform transition-transform duration-300 ease-in-out ${showPopup ? 'translate-y-0' : 'translate-y-4 opacity-0'
          }`}
        style={{
          width: isMobile ? 'calc(100% - 16px)' : '80%',
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          height: 'calc(90vh - 100px)',
        }}
      >
        <div
          style={{
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            backgroundColor: 'white',
            padding: '24px',
            width: '100%',
            height: 'calc(100% - 93px)',
          }}
        >
          {/* Header */}
          <div className="lg:flex items-center justify-between">
            <div
              className="font-inter font-medium"
              style={{ fontSize: '20px', color: '#222525' }}
            >
              {language?.['Add products'] || 'Add products'}
            </div>
            <div className="flex" style={{ alignItems: 'start', gap: '34px', marginTop: isMobile ? " 16px" : "0" }}>
              {/* Search Input */}
              <div className="relative w-[400px] h-10">
                <input
                  ref={searchInputRef}
                  style={{
                    border: '1px solid #C9D7D8',
                    padding: '8px 36px',
                    minWidth: '350px',
                    height: '40px',
                    color: '#05292A',
                    borderRadius: '12px',
                  }}
                  name="searchValue"
                  onKeyDown={onSearchSubmit}
                  value={filter.searchValue}
                  onChange={(e) => onFilterChange({ ...filter, searchValue: e.target.value })}
                  className="font-inter placeholder:text-placeholder text-main p-3 outline-none focus-none bg-transparent w-full"
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2"
                  style={{ left: '8px' }}
                >
                  <SearchIcon />
                </div>
              </div>
              {/* Close Button */}
              <button
                type="button"
                aria-label={language?.['Close'] || 'Close'}
                onClick={onClose}
                className="flex items-center justify-center cursor-pointer"
                style={{ width: '24px', height: '24px' }}
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Table */}
          <div style={{ marginTop: '20px', height: isMobile ? 'calc(100% - 66px)' : 'calc(100% - 36px)' }}>
            {/* Table Header */}
            {!isMobile &&
              <div
                className="w-full flex items-center h-10"
                style={{
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  padding: '0px 16px',
                  backgroundColor: '#EBF0F0',
                  borderLeft: '1px solid #DBE4E4',
                  borderRight: '1px solid #DBE4E4',
                  borderTop: '1px solid #DBE4E4',
                }}
              >
                {['SKU', 'Group', 'Product', 'UOM', 'Actions'].map((header, idx) => (
                  <div
                    key={header}
                    className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                    style={{
                      color: '#6A7878',
                      width: idx === 4 ? '80px' : 'calc((100% - 80px) / 4)',
                      justifyContent: idx === 4 ? 'center' : 'flex-start',
                    }}
                  >
                    {header}
                  </div>
                ))}
              </div>
            }
            {/* Table Body */}
            <div
              className="scroll-container rounded-b-md w-full"
              style={{
                height: 'calc(100% - 40px)',
                overflowY: 'auto',
                backgroundColor: '#FFFFFF',
              }}
              ref={scrollRef}
            >
              {products.map((value, idx) => {
                const baseStyle =
                  isMobile ?
                    {
                      border: "1px solid #DBE4E4",
                      borderRadius: "12px",
                      padding: '16px',
                      marginBottom: idx === products.length - 1 ? '' : '12px'
                    }
                    :
                    {
                      borderBottom: '1px solid #DBE4E4',
                      borderLeft: '1px solid #DBE4E4',
                      borderRight: '1px solid #DBE4E4',
                      borderBottomLeftRadius: idx === products.length - 1 ? '12px' : '',
                      borderBottomRightRadius: idx === products.length - 1 ? '12px' : '',
                      display: "flex",
                      height: '40px',
                      padding: '0px 16px',

                    }
                return (
                  (
                    <div
                      key={idx}
                      className="items-center"
                      style={baseStyle}
                    >
                      {
                        isMobile ?
                          <>

                            <div
                              style={{ width: '100%' }}
                              className="text-[14px] font-inter flex items-start justify-between"
                            >
                              <div>
                                <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                  {language && language?.['SKU']
                                    ? language?.['SKU']
                                    : 'SKU'}
                                </p>
                                <p style={{ color: '#222525' }}>{value.sku || '105213242'}</p>
                              </div>
                              <div style={{}} className="flex items-center justify-center">
                                <div
                                  role="checkbox"
                                  aria-checked={!!value?.isAttributes}
                                  aria-label={value.productName}
                                  tabIndex={0}
                                  onKeyDown={toggleOnSpace(() => onSelectProduct(value))}
                                  className="cursor-pointer w-5 h-5 flex items-center justify-center"
                                  onClick={() => onSelectProduct(value)}
                                  style={{
                                    border: value?.isAttributes ? '' : '1px solid #C9D7D8',
                                    backgroundColor: value?.isAttributes ? '#0F7679' : 'transparent',
                                    borderRadius: '6px'
                                  }}
                                >
                                  {value?.isAttributes ? <WhiteCheckIcon /> : null}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between" style={{ gap: '24px', margin: "12px 0px" }}>
                              <div
                                className="text-[14px] font-inter"
                                style={{ width: 'calc(50% - 12px)' }}
                              >
                                <div>
                                  <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                    {language && language?.['Group']
                                      ? language?.['Group']
                                      : 'Group'}
                                  </p>
                                  <p style={{ color: '#222525' }}>{value.groupName || '105213242'}</p>
                                </div>
                              </div>
                              <div
                                className="text-[14px] font-inter"
                                style={{ width: 'calc(50% - 12px)' }}
                              >
                                <div>
                                  <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                    {language && language?.['Product']
                                      ? language?.['Product']
                                      : 'Product'}
                                  </p>
                                  <p style={{ color: '#222525' }}>{value.productName}</p>
                                </div>
                              </div>
                            </div>
                            <div className='w-full'>
                              <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                {language && language?.['UOM']
                                  ? language?.['UOM']
                                  : 'UOM'}
                              </p>
                              <p style={{ color: '#222525' }}>{value.productName}</p>
                            </div>
                          </>
                          :
                          <>
                            <div
                              style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                              className="text-[14px] font-inter"
                            >
                              {value.sku || '105213242'}
                            </div>
                            <div
                              style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                              className="text-[14px] font-inter"
                            >
                              {value.groupName}
                            </div>
                            <div
                              style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                              className="text-[14px] font-inter"
                            >
                              {value.productName || 'Product name'}
                            </div>
                            <div
                              style={{ width: 'calc((100% - 80px) / 4)', color: '#6A7878' }}
                              className="text-[14px] font-inter"
                            >
                              {value?.uomName}
                            </div>
                            <div style={{ width: '80px' }} className="flex items-center justify-center">
                              <div
                                role="checkbox"
                                aria-checked={!!value?.isAttributes}
                                aria-label={value.productName}
                                tabIndex={0}
                                onKeyDown={toggleOnSpace(() => onSelectProduct(value))}
                                className="cursor-pointer w-5 h-5 flex items-center justify-center"
                                onClick={() => onSelectProduct(value)}
                                style={{
                                  border: value?.isAttributes ? '' : '1px solid #C9D7D8',
                                  backgroundColor: value?.isAttributes ? '#0F7679' : 'transparent',
                                  borderRadius: '6px',
                                }}
                              >
                                {value?.isAttributes ? <WhiteCheckIcon /> : null}
                              </div>
                            </div>
                          </>
                      }
                    </div>
                  )
                )
              }
              )}

              {/* Loader Trigger Element - CRITICAL: Must be visible in scroll container */}
              {loaderRef && (
                <div
                  ref={loaderRef}
                  style={{
                    height: '20px',
                    width: '100%',
                    display: 'block'
                  }}
                >
                  {loading && (
                    <div
                      className="w-full flex items-center justify-center"
                      style={{ padding: '20px 0' }}
                    >
                      <span className="loader"></span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div
          className="flex items-center justify-end"
          style={{
            gap: '12px',
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            padding: '24px',
            backgroundColor: '#F8F8F8',
            border: '1px solid #DBE4E4',
            width: '100%',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            className="font-inter cursor-pointer"
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              backgroundColor: '#FFFFFF',
              color: '#222525',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #C9D7D8',
            }}
          >
            {language?.['Cancel'] || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={onSave}
            {...saveButtonProps}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
            }}
            className="font-inter cursor-pointer"
          >
            {language?.['Save'] || 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectionModal;